/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { axiosInstance } from "@app/axios/axiosIns";

const OTHER_FIELD = "/other-field";
const TEAM_MODEL = "Team";
const SHOWCASE_MODEL = "Showcase";

export const getAllAboutUsTeams = createAsyncThunk(
  "aboutUs/teams/all",
  async (params: any, { rejectWithValue }) => {
    try {
      // `/other-field/:model` is model-based, params not used
      const response = await axiosInstance.get(`${OTHER_FIELD}/${TEAM_MODEL}`);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getAboutUsTeam = createAsyncThunk(
  "aboutUs/teams/get",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${OTHER_FIELD}/single/${id}`);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const addAboutUsTeam = createAsyncThunk(
  "aboutUs/teams/add",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(OTHER_FIELD, data);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateAboutUsTeam = createAsyncThunk(
  "aboutUs/teams/update",
  async (
    { id, data }: { id: string | undefined; data: any },
    { rejectWithValue }
  ) => {
    try {
      if (!id) throw new Error("Missing id");
      const response = await axiosInstance.patch(
        `${OTHER_FIELD}/${id}`,
        data
      );
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

export const updateAboutUsTeamSequenceBulk = createAsyncThunk(
  "aboutUs/teams/updateSequenceBulk",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${OTHER_FIELD}/updateSequence`,
        payload
      );
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

export const getAllAboutUsShowcases = createAsyncThunk(
  "aboutUs/showcase/all",
  async (params: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${OTHER_FIELD}/${SHOWCASE_MODEL}`
      );
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const getAboutUsShowcase = createAsyncThunk(
  "aboutUs/showcase/get",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${OTHER_FIELD}/single/${id}`);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const addAboutUsShowcase = createAsyncThunk(
  "aboutUs/showcase/add",
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(OTHER_FIELD, data);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

export const updateAboutUsShowcase = createAsyncThunk(
  "aboutUs/showcase/update",
  async (
    { id, data }: { id: string | undefined; data: any },
    { rejectWithValue }
  ) => {
    try {
      if (!id) throw new Error("Missing id");
      const response = await axiosInstance.patch(
        `${OTHER_FIELD}/${id}`,
        data
      );
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

export const deleteOtherFieldById = createAsyncThunk(
  "aboutUs/otherField/delete",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`${OTHER_FIELD}/${id}`);
      return response?.data;
    } catch (error: any) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  }
);

interface AboutUsState {
  isLoading: boolean;
  formLoader: boolean;
  teams: any;
  team: any;
  showcases: any;
  showcase: any;
  error: any;
}

const initialState: AboutUsState = {
  isLoading: false,
  formLoader: false,
  teams: [],
  team: null,
  showcases: [],
  showcase: null,
  error: null,
};

const aboutUsSlice: any = createSlice({
  name: "aboutUs",
  initialState,
  reducers: { resetState: () => initialState },
  extraReducers: (builder) => {
    builder
      // Teams
      .addCase(getAllAboutUsTeams.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllAboutUsTeams.fulfilled, (state, action) => {
        state.isLoading = false;
        const item = action.payload?.data ?? action.payload;
        state.teams = {
          data: item ? [item] : [],
          totalCount: item ? 1 : 0,
        };
      })
      .addCase(getAllAboutUsTeams.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(getAboutUsTeam.pending, (state) => {
        state.isLoading = true;
        state.team = null;
      })
      .addCase(getAboutUsTeam.fulfilled, (state, action) => {
        state.isLoading = false;
        state.team = (action.payload?.data ?? action.payload) || null;
      })
      .addCase(getAboutUsTeam.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(addAboutUsTeam.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(addAboutUsTeam.fulfilled, (state, action) => {
        state.formLoader = false;
        state.team = (action.payload?.data ?? action.payload) || null;
      })
      .addCase(addAboutUsTeam.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(updateAboutUsTeam.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(updateAboutUsTeam.fulfilled, (state, action) => {
        state.formLoader = false;
        state.team = (action.payload?.data ?? action.payload) || null;
      })
      .addCase(updateAboutUsTeam.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(updateAboutUsTeamSequenceBulk.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(updateAboutUsTeamSequenceBulk.fulfilled, (state) => {
        state.formLoader = false;
      })
      .addCase(updateAboutUsTeamSequenceBulk.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteOtherFieldById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteOtherFieldById.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteOtherFieldById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })

      // Showcase
      .addCase(getAllAboutUsShowcases.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllAboutUsShowcases.fulfilled, (state, action) => {
        state.isLoading = false;
        const item = action.payload?.data ?? action.payload;
        state.showcases = {
          data: item ? [item] : [],
          totalCount: item ? 1 : 0,
        };
      })
      .addCase(getAllAboutUsShowcases.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(getAboutUsShowcase.pending, (state) => {
        state.isLoading = true;
        state.showcase = null;
      })
      .addCase(getAboutUsShowcase.fulfilled, (state, action) => {
        state.isLoading = false;
        state.showcase = (action.payload?.data ?? action.payload) || null;
      })
      .addCase(getAboutUsShowcase.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(addAboutUsShowcase.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(addAboutUsShowcase.fulfilled, (state, action) => {
        state.formLoader = false;
        state.showcase = (action.payload?.data ?? action.payload) || null;
      })
      .addCase(addAboutUsShowcase.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload || action.error.message;
      })
      .addCase(updateAboutUsShowcase.pending, (state) => {
        state.formLoader = true;
      })
      .addCase(updateAboutUsShowcase.fulfilled, (state, action) => {
        state.formLoader = false;
        state.showcase = (action.payload?.data ?? action.payload) || null;
      })
      .addCase(updateAboutUsShowcase.rejected, (state, action) => {
        state.formLoader = false;
        state.error = action.payload || action.error.message;
      });
  },
});

export const { resetState } = aboutUsSlice.actions;
export default aboutUsSlice.reducer;

